// Session state for the app. Loads /api/auth/me on mount; exposes login/logout.
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { AuthUser, MeResponse } from '@alignova/shared';
import { api, ApiError } from '../lib/api.ts';

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<MeResponse>('/auth/me')
      .then((res) => setUser(res.user))
      .catch((err: unknown) => {
        if (!(err instanceof ApiError) || err.status !== 401) console.error(err);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  async function login(username: string, password: string): Promise<void> {
    const res = await api.post<{ user: AuthUser }>('/auth/login', { username, password });
    setUser(res.user);
  }

  async function logout(): Promise<void> {
    await api.post('/auth/logout');
    setUser(null);
  }

  return <AuthContext value={{ user, loading, login, logout }}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
